import React, { useState } from "react";
import ComponentCustomModal from "../../../components/common/customModal/ComponentCustomModal";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/common/Loader";
import { FieldArray, Form, Formik } from "formik";
import useDataInputOption from "../../../hooks/useDataInputOption";
import { FtextField } from "../../../components/common/FtextField";
import { useDispatch, useSelector } from "react-redux";
import {
  assignNetworkDecice,
  reassignNetworkDiagramDevice,
} from "../../../features/oltApi";

const AssignDeviceDialog = ({ show, setShow, isUpdate, parent, children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //---> Get netwok device from redux store
  const devices = useSelector((state) => state.network?.devices);

  //---> Get network device outputs form redux store
  const outputs = useSelector((state) => state.network?.outputs);

  //---> Get customers form redux store
  const customers = useSelector((state) => state.customer.customer);

  //---> Local State
  const [isLoading, setIsLoading] = useState(false);
  const [deviceOutputs, setDeviceOutput] = useState([]);
  const [filterDevice, setFilterDevice] = useState([]);

  const handleDeviceOutput = (id) => {
    const output = outputs.filter((item) => item.netWorkDevice === id);
    setDeviceOutput(output);
  };

  const handleGetDevicesByDeviceType = (value) => {
    const filteredDevices = devices.filter(
      (item) => item.candidateType === value
    );

    setFilterDevice(filteredDevices);
  };

  const extraInputOptions = [
    {
      as: "select",
      id: "deviceType",
      name: "deviceType",
      label: t("deviceType"),
      firstOptions: t("selectDeviceType"),
      textAccessor: "name",
      valueAccessor: "value",
      options: [
        {
          name: t("mikrotik"),
          value: "mikrotik",
        },
        {
          name: t("olt"),
          value: "olt",
        },
        {
          name: t("switch"),
          value: "switch",
        },
        {
          name: t("splitter"),
          value: "splitter",
        },
        {
          name: t("onu"),
          value: "onu",
        },
      ],
      validation: true,
      isVisible: true,
      disabled: false,
      extraOnChange: (value) => handleGetDevicesByDeviceType(value),
    },
    {
      name: "device",
      as: "select",
      type: "select",
      id: "device",
      isVisible: true,
      disabled: false,
      validation: true,
      label: t("device"),
      firstOptions: t("selectDevice"),
      textAccessor: "name",
      valueAccessor: "id",
      options: filterDevice,
      extraOnChange: (value) => handleDeviceOutput(value),
    },
    {
      name: "customer",
      as: "select",
      type: "select",
      id: "customer",
      disabled: false,
      validation: true,
      visibleKey: "deviceType",
      visibleVlaue: "onu",
      label: t("customer"),
      firstOptions: t("customer"),
      textAccessor: "name",
      valueAccessor: "id",
      options: customers,
    },
    {
      name: "location",
      type: "text",
      id: "location",
      isVisible: true,
      disabled: false,
      validation: false,
      label: t("location"),
    },
  ];

  // call the data input option function
  const inputPermission = {
    device: true,
    location: true,
  };

  // get data input option from useDataInputOption hook
  const dataInputOption = useDataInputOption(
    inputPermission,
    null,
    null,
    null,
    extraInputOptions
  );

  const findDeviceById = (id) => {
    const device = devices.find((item) => item.id === id);

    return device;
  };

  const findDeviceOutputById = (id) => {
    const output = outputs.find((item) => item.id === id);

    return output;
  };

  const handleNetworkAssignFormSubmission = (formValues) => {
    if (isUpdate) {
      const sendingData = {
        ...formValues,
        parent: parent.data._id,
      };

      reassignNetworkDiagramDevice(
        dispatch,
        sendingData,
        setIsLoading,
        setShow
      );
    } else {
      const sendingData = {
        ...formValues,
        parent: parent._id,
      };

      assignNetworkDecice(dispatch, sendingData, setIsLoading, setShow);
    }
  };

  return (
    <>
      <ComponentCustomModal
        {...{
          show,
          setShow,
          size: "lg",
          header: isUpdate ? t("assingNewDevice") : t("reassingNewDevice"),
          footer: (
            <div className="displayGrid1 float-end">
              <button
                type="button"
                className="btn btn-secondary border-0"
                disabled={isLoading}
                onClick={() => setShow(false)}
              >
                {t("cancel")}
              </button>

              <button
                type="submit"
                form="assignDeviceForm"
                className="btn btn-success border-0"
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : t("save")}
              </button>
            </div>
          ),
        }}
      >
        <Formik
          initialValues={dataInputOption.inputInitialValues}
          onSubmit={(values) => {
            handleNetworkAssignFormSubmission(values);
          }}
          enableReinitialize
        >
          {() => (
            <Form id="assignDeviceForm">
              <div className="displayGrid">
                <div className="displayGrid2">
                  {dataInputOption.inputOption?.map(
                    (item) =>
                      item?.isVisible && (
                        <FtextField key={item.name} {...item} />
                      )
                  )}
                </div>

                {children?.length > 0 && (
                  <FieldArray name="outputs">
                    {({ form }) =>
                      children?.map((child, index) => {
                        console.log({ child });

                        const parentDevice = parent
                          ? findDeviceById(parent.data.device)
                          : null;

                        const output = child
                          ? findDeviceOutputById(child.data.output)
                          : null;

                        const childrenDevice = child
                          ? findDeviceById(child.data.device)
                          : null;

                        const displayText =
                          parentDevice && output && childrenDevice
                            ? `${parentDevice.name} output ${output.portName}-(${childrenDevice.name})`.toUpperCase()
                            : "N/A";

                        return (
                          <div
                            key={index}
                            className="gridTemplateManualColumn4_6"
                          >
                            <div>
                              <FtextField
                                name={`outputs.${index}.parent`}
                                as="select"
                                id={`outputs-${index}-parent`}
                                options={deviceOutputs?.map(
                                  ({ portName, id }) => ({
                                    text: portName,
                                    value: id,
                                  })
                                )}
                                firstOptions={t("bandwidth")}
                                textAccessor="text"
                                valueAccessor="value"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  form.setFieldValue(
                                    `outputs.${index}.parent`,
                                    value
                                  );

                                  form.setFieldValue(
                                    `outputs.${index}.output`,
                                    child?.data._id
                                  );
                                }}
                              />
                            </div>

                            <div>
                              <FtextField
                                name={`outputs.${index}.output`}
                                as="select"
                                id={`outputs-${index}-output`}
                                options={[
                                  {
                                    text: displayText,
                                    value: child?.data._id || "",
                                  },
                                ]}
                                textAccessor="text"
                                valueAccessor="value"
                                disabled={true}
                                style={{ cursor: "not-allowed" }}
                              />
                            </div>
                          </div>
                        );
                      })
                    }
                  </FieldArray>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </ComponentCustomModal>
    </>
  );
};

export default AssignDeviceDialog;
